import { useContext, useEffect, useState } from 'react';
import { clone } from 'lodash';
import { Storefront } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { IconTitle } from '../../../../components';
import { MarketContext, useMarketContext } from '../../../../contexts/MarketContext';
import useStyles from './styles';
import Market, { MarketType } from '../../../../entities/market.entity';
import Form from './components';
import { Condominium, Contractor } from '../../../../entities';
import { useCondominiums, useContractors, useFetch } from '../../../../hooks';
import { RecursiveKeyOf } from '../../../../utils/types';
import { set } from '../../../../utils/objects';
import useMarkets from '../../../../hooks/data/useMarkets';

const initialMarket = {
  type: MarketType.External,
};

export const BasicData = (): JSX.Element => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { market: loadMarket, handleSelectMarket, setLoading, loading } = useMarketContext();

  const { enqueueSnackbar } = useSnackbar();
  const [market, setMarket] = useState<Market>(loadMarket || (initialMarket as Market));
  const [condominium, setCondominium] = useState<Condominium[]>([]);
  const [contractor, setContractor] = useState<Contractor[]>([]);

  const condominiumRepository = useCondominiums();
  const contractorRepository = useContractors();
  const marketRepository = useMarkets();

  const { data: condominiumList, loading: condominiumLoading } = useFetch(condominiumRepository.fetchAll);
  const { data: contractorList, loading: contractorLoading } = useFetch(contractorRepository.fetchAll);

  useEffect(() => {
    setCondominium(condominiumList ?? []);
    setContractor(contractorList ?? []);
    setLoading(condominiumLoading && contractorLoading);
  }, [condominiumList, contractorList, setLoading, condominiumLoading, contractorLoading]);

  useEffect(() => {
    setMarket(clone(loadMarket) || (initialMarket as Market));
  }, [loadMarket]);

  const handleFieldChange = (key: RecursiveKeyOf<Market>, value: string | null | number | boolean | string[]) => {
    const newMarket = set(market, key, value);
    setMarket(newMarket);
  };

  const handleCreateMarket = async () => {
    const { id } = await marketRepository.create(market);
    navigate(`/markets/${id}`);
  };

  const handleUpdateMarket = async () => {
    await marketRepository.update(market);
    handleSelectMarket(market);
  };

  const handleSubmit = async () => {
    try {
      if (market.id) {
        await handleUpdateMarket();
      } else {
        await handleCreateMarket();
      }
      enqueueSnackbar('Mercado salvo com sucesso!', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Não foi possível salvar, tente novamente mais tarde', { variant: 'error' });
    }
  };

  if (loading) {
    return <></>;
  }

  return (
    <div className={styles.container}>
      <IconTitle title="Dados básicos" IconComponent={Storefront} />
      <Form
        market={market}
        condominium={condominium}
        contractor={contractor}
        onSubmit={handleSubmit}
        handleFieldChange={handleFieldChange}
      />
    </div>
  );
};

export default BasicData;
